import { useEffect, useState } from 'react';
import { fetchSlots, fetchBidsForSlot, createSlot, createBid, updateBidStatus, createPlacement, type AdSlot, type AdBid } from '../../features/ads/api';
import { Button } from '../../components/ui/Button';

type View = 'slots' | 'my-bids' | 'admin' | 'create-slot' | 'bid-on-slot';

export function AdsPage() {
  const [currentView, setCurrentView] = useState<View>('slots');
  const [userAlias, setUserAlias] = useState(localStorage.getItem('ads.userAlias') || '');
  const [selectedSlot, setSelectedSlot] = useState<AdSlot | null>(null);

  if (!userAlias) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Advertisement Platform</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">Enter your alias to access advertising features</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const alias = formData.get('alias') as string;
              if (alias.trim()) {
                localStorage.setItem('ads.userAlias', alias.trim());
                setUserAlias(alias.trim());
              }
            }}
          >
            <input
              name="alias"
              type="text"
              placeholder="Your alias"
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent mb-4"
              required
            />
            <Button type="submit" className="w-full">
              Continue
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Advertisement Platform</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">Managing as: {userAlias}</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentView('slots')}
                className={currentView === 'slots' ? 'bg-brand-600' : ''}
                variant={currentView === 'slots' ? undefined : 'secondary'}
              >
                Browse Slots
              </Button>
              <Button
                onClick={() => setCurrentView('my-bids')}
                className={currentView === 'my-bids' ? 'bg-brand-600' : ''}
                variant={currentView === 'my-bids' ? undefined : 'secondary'}
              >
                My Bids
              </Button>
              <Button
                onClick={() => setCurrentView('admin')}
                className={currentView === 'admin' ? 'bg-brand-600' : ''}
                variant={currentView === 'admin' ? undefined : 'secondary'}
              >
                Admin
              </Button>
              <Button
                onClick={() => {
                  localStorage.removeItem('ads.userAlias');
                  setUserAlias('');
                }}
                variant="secondary"
              >
                Change Alias
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentView === 'slots' && <SlotBrowser onSelectSlot={(slot) => { setSelectedSlot(slot); setCurrentView('bid-on-slot'); }} />}
        {currentView === 'my-bids' && <MyBidsView userAlias={userAlias} />}
        {currentView === 'admin' && <AdminDashboard onCreateSlot={() => setCurrentView('create-slot')} />}
        {currentView === 'create-slot' && <CreateSlotForm onSuccess={() => setCurrentView('admin')} onCancel={() => setCurrentView('admin')} />}
        {currentView === 'bid-on-slot' && selectedSlot && (
          <BidForm
            slot={selectedSlot}
            userAlias={userAlias}
            onSuccess={() => setCurrentView('my-bids')}
            onCancel={() => setCurrentView('slots')}
          />
        )}
      </div>
    </div>
  );
}

function SlotBrowser({ onSelectSlot }: { onSelectSlot: (slot: AdSlot) => void }) {
  const [slots, setSlots] = useState<AdSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSlots();
  }, []);

  async function loadSlots() {
    try {
      setLoading(true);
      const data = await fetchSlots(100);
      setSlots(data.filter(s => s.status === 'open'));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load slots');
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="text-center text-slate-600 dark:text-slate-400">Loading available ad slots...</div>;
  if (error) return <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">Error: {error}</div>;
  if (slots.length === 0) return <div className="text-center py-12 text-slate-600 dark:text-slate-400">No open ad slots available</div>;

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Available Ad Slots</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {slots.map((slot) => (
          <div key={slot.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">{slot.name}</h3>
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400 text-xs rounded">
                {slot.type}
              </span>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              <div>Topic: {slot.topicId}</div>
              <div>Min Bid: {slot.currency} {(slot.minBidCents / 100).toFixed(2)}</div>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-500 mb-3">
              <div>Start: {new Date(slot.startTime).toLocaleDateString()}</div>
              <div>End: {new Date(slot.endTime).toLocaleDateString()}</div>
            </div>
            <Button onClick={() => onSelectSlot(slot)} className="w-full text-sm py-1.5">
              Place Bid
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BidForm({ slot, userAlias, onSuccess, onCancel }: { slot: AdSlot; userAlias: string; onSuccess: () => void; onCancel: () => void }) {
  const [amount, setAmount] = useState('');
  const [creativeUrl, setCreativeUrl] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bids, setBids] = useState<AdBid[]>([]);

  useEffect(() => {
    loadBids();
  }, [slot.id]);

  async function loadBids() {
    try {
      const data = await fetchBidsForSlot(slot.id, 10);
      setBids(data);
    } catch (err) {
      console.error('Failed to load bids', err);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (isNaN(amountCents) || amountCents < slot.minBidCents) {
      setError(`Minimum bid is ${slot.currency} ${(slot.minBidCents / 100).toFixed(2)}`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await createBid({
        slotId: slot.id,
        bidderAlias: userAlias,
        amountCents,
        currency: slot.currency,
        creativeUrl: creativeUrl || undefined,
        targetUrl: targetUrl || undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit bid');
    } finally {
      setSubmitting(false);
    }
  }

  const highestBid = bids.length > 0 ? bids[0] : null;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Place Bid on "{slot.name}"</h2>

        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-600 dark:text-slate-400">Type:</span>
              <span className="text-slate-900 dark:text-slate-100 ml-2">{slot.type}</span>
            </div>
            <div>
              <span className="text-slate-600 dark:text-slate-400">Topic:</span>
              <span className="text-slate-900 dark:text-slate-100 ml-2">{slot.topicId}</span>
            </div>
            <div>
              <span className="text-slate-600 dark:text-slate-400">Min Bid:</span>
              <span className="text-slate-900 dark:text-slate-100 ml-2">{slot.currency} {(slot.minBidCents / 100).toFixed(2)}</span>
            </div>
            <div>
              <span className="text-slate-600 dark:text-slate-400">Current High Bid:</span>
              <span className="text-green-700 dark:text-green-400 ml-2 font-semibold">
                {highestBid ? `${slot.currency} ${(highestBid.amountCents / 100).toFixed(2)}` : 'None'}
              </span>
            </div>
          </div>
        </div>

        {highestBid && (
          <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 rounded-lg p-3 mb-4">
            <div className="text-yellow-700 dark:text-yellow-400 text-sm">
              Current winning bid: <strong>{slot.currency} {(highestBid.amountCents / 100).toFixed(2)}</strong> by {highestBid.bidderAlias}
            </div>
          </div>
        )}

        {error && <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded p-3 mb-4 text-red-700 dark:text-red-400 text-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bid Amount ({slot.currency}) *</label>
            <input
              type="number"
              step="0.01"
              min={(slot.minBidCents / 100).toFixed(2)}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder={(slot.minBidCents / 100).toFixed(2)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Creative URL (Image/Video)</label>
            <input
              type="url"
              value={creativeUrl}
              onChange={(e) => setCreativeUrl(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="https://example.com/banner.jpg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Target URL (Where ad clicks go)</label>
            <input
              type="url"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="https://your-site.com"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Bid'}
            </Button>
            <Button type="button" onClick={onCancel} variant="secondary">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MyBidsView({ userAlias }: { userAlias: string }) {
  const [slots, setSlots] = useState<AdSlot[]>([]);
  const [allBids, setAllBids] = useState<{ [slotId: string]: AdBid[] }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyBids();
  }, [userAlias]);

  async function loadMyBids() {
    try {
      setLoading(true);
      const slotsData = await fetchSlots(100);
      setSlots(slotsData);

      const bidsMap: { [slotId: string]: AdBid[] } = {};
      for (const slot of slotsData) {
        const bids = await fetchBidsForSlot(slot.id, 50);
        bidsMap[slot.id] = bids.filter(b => b.bidderAlias === userAlias);
      }
      setAllBids(bidsMap);
    } catch (err) {
      console.error('Failed to load bids', err);
    } finally {
      setLoading(false);
    }
  }

  const myBids = Object.entries(allBids).flatMap(([slotId, bids]) =>
    bids.map(bid => ({ ...bid, slot: slots.find(s => s.id === slotId) }))
  );

  if (loading) return <div className="text-center text-slate-600 dark:text-slate-400">Loading your bids...</div>;
  if (myBids.length === 0) return <div className="text-center py-12 text-slate-600 dark:text-slate-400">You haven't placed any bids yet</div>;

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">My Bids</h2>
      <div className="space-y-3">
        {myBids.map((bid) => (
          <div key={bid.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">{bid.slot?.name || 'Unknown Slot'}</h3>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Amount: {bid.currency} {(bid.amountCents / 100).toFixed(2)}
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                bid.status === 'winning' ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400' :
                bid.status === 'accepted' ? 'bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400' :
                bid.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400' :
                'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400'
              }`}>
                {bid.status}
              </span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-500">
              Bid placed: {new Date(bid.bidTime).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminDashboard({ onCreateSlot }: { onCreateSlot: () => void }) {
  const [slots, setSlots] = useState<AdSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AdSlot | null>(null);
  const [bids, setBids] = useState<AdBid[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSlots();
  }, []);

  async function loadSlots() {
    try {
      setLoading(true);
      const data = await fetchSlots(100);
      setSlots(data);
    } catch (err) {
      console.error('Failed to load slots', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadBids(slotId: string) {
    try {
      const data = await fetchBidsForSlot(slotId, 100);
      setBids(data);
    } catch (err) {
      console.error('Failed to load bids', err);
    }
  }

  async function handleAcceptBid(bidId: string) {
    try {
      await updateBidStatus(bidId, 'accepted');
      if (selectedSlot) loadBids(selectedSlot.id);
    } catch (err) {
      console.error('Failed to accept bid', err);
    }
  }

  async function handleRejectBid(bidId: string) {
    try {
      await updateBidStatus(bidId, 'rejected');
      if (selectedSlot) loadBids(selectedSlot.id);
    } catch (err) {
      console.error('Failed to reject bid', err);
    }
  }

  if (loading) return <div className="text-center text-slate-600 dark:text-slate-400">Loading admin dashboard...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Admin Dashboard</h2>
        <Button onClick={onCreateSlot}>+ Create Ad Slot</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Slots List */}
        <div className="lg:col-span-1">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Ad Slots</h3>
          <div className="space-y-2">
            {slots.map((slot) => (
              <button
                key={slot.id}
                onClick={() => { setSelectedSlot(slot); loadBids(slot.id); }}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedSlot?.id === slot.id
                    ? 'bg-brand-600 text-white'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                }`}
              >
                <div className="font-medium">{slot.name}</div>
                <div className="text-xs opacity-75">{slot.status}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Bids for Selected Slot */}
        <div className="lg:col-span-2">
          {selectedSlot ? (
            <>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">
                Bids for "{selectedSlot.name}"
              </h3>
              {bids.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-500">No bids yet</div>
              ) : (
                <div className="space-y-2">
                  {bids.map((bid, idx) => (
                    <div key={bid.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {idx === 0 && <span className="text-yellow-400">👑</span>}
                          <div>
                            <div className="font-semibold text-slate-900 dark:text-slate-100">
                              {selectedSlot.currency} {(bid.amountCents / 100).toFixed(2)}
                            </div>
                            <div className="text-sm text-slate-600 dark:text-slate-400">by {bid.bidderAlias}</div>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          bid.status === 'accepted' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                          bid.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}>
                          {bid.status}
                        </span>
                      </div>
                      {bid.targetUrl && (
                        <div className="text-xs text-slate-500 dark:text-slate-500 mb-2">
                          Target: {bid.targetUrl}
                        </div>
                      )}
                      {bid.status === 'pending' && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            onClick={() => handleAcceptBid(bid.id)}
                            className="text-xs py-1 px-3 bg-green-700 hover:bg-green-600"
                          >
                            Accept
                          </Button>
                          <Button
                            onClick={() => handleRejectBid(bid.id)}
                            className="text-xs py-1 px-3 bg-red-700 hover:bg-red-600"
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-600 dark:text-slate-400">
              Select a slot to view bids
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateSlotForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('banner');
  const [topicId, setTopicId] = useState('');
  const [minBid, setMinBid] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const minBidCents = Math.round(parseFloat(minBid) * 100);
    if (isNaN(minBidCents) || minBidCents <= 0) {
      setError('Please enter a valid minimum bid');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await createSlot({
        name: name.trim(),
        type,
        topicId: topicId.trim(),
        minBidCents,
        currency,
        startTime,
        endTime,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create slot');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Create Ad Slot</h2>

        {error && <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded p-3 mb-4 text-red-700 dark:text-red-400 text-sm">{error}</div>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Slot Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="e.g., General Topic Banner"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Type *</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="banner">Banner</option>
                <option value="inline">Inline</option>
                <option value="spotlight">Spotlight</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Topic ID *</label>
              <input
                type="text"
                value={topicId}
                onChange={(e) => setTopicId(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="general"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Min Bid *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={minBid}
                onChange={(e) => setMinBid(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="100.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Start Time *</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">End Time *</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Slot'}
            </Button>
            <Button type="button" onClick={onCancel} variant="secondary">
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
